import { isNil, pick, propEq } from "ramda";
import { v4 as uuidv4 } from "uuid";

import {
  lockRoute,
  setRequests,
  setRequestStatus,
  setScheduleRequired,
  updateStop,
  setStops,
} from "./route";
import {
  setDeferRequest,
  setRejectRequest,
  updateRouteRequestsWithMapper,
  generateTransferResponse,
  generateMergeToStopRequests,
  getValidateParcelList,
  generateRequest,
  setResponse,
  filterPendingRequests,
  isRequestFromRoutePlanner,
  isAddRequest,
  getUpdatedParcels,
  isShopUnsuitableRequest,
  getRequestsParcelCodes,
  getRequestsByParcelCodes,
  getDeferredRequests,
  resetResponse,
} from "../../models/request";
import { DISMISS } from "../../constants/strings";
import { validateStopMerge, processRequestStop } from "./stop";
import {
  getCurrentRouteId,
  getRouteRequests,
  getRouteStops,
  getRouteDetail,
  getRequestOrder,
} from "../selectors/route";
import {
  requestOriginTypes,
  requestTypes,
  responseTypes,
} from "../../enums/requestTypes";
import { showNotification } from "./notifications";
import { NotificationType } from "../../features/Notifier";
import {
  createCollectionStop,
  getHighestSequenceNumber,
  getHighestStopNumber,
  getStopParcelCodes,
} from "../../models/stop";
import { arrayToObject } from "../../utils/array";
import { getRoutesTransfer } from "../../services/routes";
import { prepareTransferDriversRoutes } from "../../features/request/model";
import { getStopByNumber } from "../../features/routeDetail/model";
import { clearSelection } from "./ui/routeDetail";
import { originalTypes } from "../../enums/stopTypes";
import { getErrorMessage } from "../../utils/errors";

const updateRouteRequests = (updatedRequests) => (dispatch) => {
  dispatch(setRequests(updatedRequests));
  dispatch(setScheduleRequired(true));
};

const createRequestAction = (mapper) => (requests) => async (
  dispatch,
  getState
) => {
  const currentUserLock = await dispatch(lockRoute());

  if (!currentUserLock) {
    return false;
  }

  const routeRequests = getRouteRequests(getState());
  const updateRequestsMap = arrayToObject(requests, "requestId");

  routeRequests.forEach((request) => {
    const { requestId } = request;
    const requestInMap = updateRequestsMap[requestId];

    if (!requestInMap) {
      updateRequestsMap[requestId] = request;
    } else if (mapper) {
      updateRequestsMap[requestId] = mapper(requestInMap);
    }
  });

  dispatch(updateRouteRequests(Object.values(updateRequestsMap)));
  return true;
};

export const onDeferRequest = createRequestAction(setDeferRequest);

export const resetAllRequestsExceptDeferred = () => (dispatch, getState) => {
  const state = getState();
  const routeRequests = getRouteRequests(state);
  const deferredRequests = getDeferredRequests(routeRequests);
  const updatedRequests = deferredRequests.map((request) =>
    resetResponse(request)
  );

  dispatch(setRequests(updatedRequests));
};

export const onRejectRequest = (requests) => async (dispatch, getState) => {
  const currentUserLock = await dispatch(lockRoute());

  const state = getState();

  if (!currentUserLock) {
    return false;
  }

  let routeRequests = getRouteRequests(state);
  const requestsFromRoutePlanner = requests.filter(isRequestFromRoutePlanner);

  if (requestsFromRoutePlanner.length) {
    const sameRequestFromSaturn = getRequestsByParcelCodes(
      routeRequests,
      getRequestsParcelCodes(requests),
      requestOriginTypes.SATURN
    );
    if (sameRequestFromSaturn) {
      routeRequests = await dispatch(onRejectRequest(sameRequestFromSaturn));
    }
    const routeDetail = getRouteDetail(state);
    const stop = getStopByNumber(routeDetail.stop, requests[0].stopNumber);
    const toValidateParcelList =
      (stop && getValidateParcelList(stop, requestsFromRoutePlanner)) || [];

    if (toValidateParcelList.length > 0) {
      try {
        const updatedStop = await dispatch(
          validateStopMerge({
            parcels: toValidateParcelList,
            checkOpenRequests: false,
          })
        );
        dispatch(updateStop({ ...stop, ...updatedStop }));
      } catch (error) {
        dispatch(
          showNotification({
            type: NotificationType.ACTION,
            message: getErrorMessage(error),
            buttonText: DISMISS,
          })
        );
        return null;
      }
    } else {
      dispatch(clearSelection());
      stop && dispatch(processRequestStop(stop, requests));
    }
  }
  const updatedRequests = updateRouteRequestsWithMapper(
    routeRequests,
    requests,
    setRejectRequest
  );
  dispatch(updateRouteRequests(updatedRequests));
  return updatedRequests;
};

export const getAvailableRoutesTransfer = (
  depot,
  date,
  uid,
  uniqueNextWaveRoutes = []
) => async (dispatch, getState) => {
  try {
    const state = getState();
    const routes = await getRoutesTransfer(depot, date, uid);
    const currentRouteId = getCurrentRouteId(state);

    return prepareTransferDriversRoutes(
      routes,
      currentRouteId,
      uniqueNextWaveRoutes
    );
  } catch {
    return [];
  }
};

export const onConfirmTransferRequest = (row, selectedRouteId) => async (
  dispatch,
  getState
) => {
  const currentUserLock = await dispatch(lockRoute());
  const { requests } = row;
  if (!currentUserLock) {
    return;
  }
  const state = getState();
  const routeDetail = getRouteDetail(state);
  const stop = getStopByNumber(routeDetail.stop, requests[0].stopNumber);
  const toValidateParcelList =
    (stop && getValidateParcelList(stop, requests)) || [];

  if (
    requests[0].requestType !== requestTypes.REMOVE_COLLECTION &&
    toValidateParcelList.length > 0
  ) {
    try {
      const updatedStop = await dispatch(
        validateStopMerge({
          parcels: toValidateParcelList,
          checkOpenRequests: false,
        })
      );
      dispatch(updateStop({ ...stop, ...updatedStop }));
    } catch (error) {
      dispatch(
        showNotification({
          type: NotificationType.ACTION,
          message: getErrorMessage(error),
          buttonText: DISMISS,
        })
      );
      return null;
    }
  } else {
    dispatch(clearSelection());
    stop && dispatch(processRequestStop(stop, requests));
  }

  const routeId = getCurrentRouteId(state);
  const routeRequests = getRouteRequests(state);
  const requestOrder = getRequestOrder(state);

  const addRequests = isAddRequest(requests[0]);
  const updatedRequests = updateRouteRequestsWithMapper(
    routeRequests,
    requests,
    generateTransferResponse(
      routeId,
      selectedRouteId,
      requests[0].stopNumber,
      addRequests ? responseTypes.REJECT : responseTypes.ACCEPT
    )
  );
  const newRequests = generateMergeToStopRequests(
    requests,
    selectedRouteId,
    requestOrder
  );

  dispatch(updateRouteRequests(updatedRequests.concat(newRequests)));
  return newRequests;
};

export const onAcceptRequest = ({ requests, ...stop }) => async (
  dispatch,
  getState
) => {
  const currentUserLock = await dispatch(lockRoute());
  if (!currentUserLock) {
    return;
  }

  const state = getState();
  const routeId = getCurrentRouteId(state);
  const firstRequest = requests[0];
  const acceptedRequest = responseTypes.ACCEPT;

  let stops = getRouteStops(state).slice();
  const updateStopIndex = stops.findIndex(
    propEq("stopNumber", firstRequest.stopNumber)
  );
  const updatedRequests = [];
  const removedParcels = [];
  let validateParcels = [];

  await dispatch(setRequestStatus(true));

  switch (firstRequest.requestType) {
    case requestTypes.AMEND_PICKUP_PARCELS: {
      if (!isNil(firstRequest.address)) {
        const stopNumber = getHighestStopNumber(stops) + 1;
        const parcel = {
          labelNumber: firstRequest.labelNumber,
          parcelCode: firstRequest.parcelCode,
        };
        const addParcelRequest = setResponse({
          responseType: acceptedRequest,
          newStopNumber: stopNumber,
          originalStopNumber: firstRequest.stopNumber,
          originalRouteId: routeId,
          newRouteId: routeId,
        })(
          generateRequest({
            requestType: requestTypes.ADD_PARCEL,
            parcel,
            stop: {
              ...stop,
              stopNumber,
            },
          })
        );

        updatedRequests.push(addParcelRequest);
        const parcelForRemove =
          updateStopIndex !== -1 &&
          stops[updateStopIndex].parcel.find(
            propEq("parcelCode", firstRequest.parcelCode)
          );
        parcelForRemove && removedParcels.push(parcelForRemove.parcelCode);
      }

      const remainingStopParcels = stops[updateStopIndex].parcel.filter(
        ({ parcelCode }) => parcelCode !== firstRequest.parcelCode
      );

      if (!remainingStopParcels.length) {
        stops = stops.filter(
          (stop) => stop.stopNumber !== stops[updateStopIndex].stopNumber
        );
      } else {
        validateParcels = getStopParcelCodes({ parcel: remainingStopParcels });
      }

      updatedRequests.push(
        setResponse({
          responseType: acceptedRequest,
          originalStopNumber: firstRequest.stopNumber,
          originalRouteId: routeId,
          newRouteId: !isNil(firstRequest.address) ? routeId : "GOH",
        })(firstRequest)
      );
      break;
    }
    case requestTypes.ADD_COLLECTION: {
      const existingStopNumber = stops.findIndex(
        ({ stopNumber }) => firstRequest.stopNumber === stopNumber
      );

      if (existingStopNumber === -1) {
        const stopNumber = getHighestStopNumber(stops) + 1;
        const sequenceNumber = getHighestSequenceNumber(stops) + 1;

        stops.push({
          ...createCollectionStop(firstRequest),
          stopId: uuidv4(),
          stopNumber,
          sequenceNumber,
          displayNumber: `${sequenceNumber}`,
        });
      }

      updatedRequests.push(
        setResponse({
          responseType: acceptedRequest,
          originalStopNumber: firstRequest.stopNumber,
          newStopNumber: stop.stopNumber,
          newRouteId: routeId,
        })(firstRequest)
      );
      break;
    }
    default: {
      let existingRequests = getRouteRequests(state);
      const isRoutePlannerRequest = isRequestFromRoutePlanner(requests[0]);
      const sameRequest = getRequestsByParcelCodes(
        existingRequests,
        getRequestsParcelCodes(requests),
        isRoutePlannerRequest
          ? requestOriginTypes.SATURN
          : requestOriginTypes.ROUTE_PLANNER
      );
      const allRequests = sameRequest.length
        ? await dispatch(
            onRejectRequest(isRoutePlannerRequest ? sameRequest : requests)
          )
        : existingRequests;

      if (!isRoutePlannerRequest && sameRequest.length) {
        return dispatch(onAcceptRequest({ ...stop, requests: sameRequest }));
      }

      if (updateStopIndex > -1) {
        validateParcels = filterPendingRequests(
          getStopParcelCodes(stops[updateStopIndex]),
          allRequests
        );
      }

      requests.forEach((request) => {
        validateParcels.push(request.parcelCode);
        updatedRequests.push(
          setResponse({
            responseType: acceptedRequest,
            originalStopNumber: request.stopNumber,
            newStopNumber: stop.stopNumber,
            newRouteId: routeId,
          })(request)
        );
      });
    }
  }

  try {
    if (validateParcels.length) {
      const stopFromApi = await dispatch(
        validateStopMerge({
          parcels: validateParcels,
          checkOpenRequests: false,
        })
      );

      if (updateStopIndex > -1) {
        stops[updateStopIndex] = {
          ...stops[updateStopIndex],
          ...stopFromApi,
          parcel: isAddRequest(firstRequest)
            ? getUpdatedParcels(
                stops[updateStopIndex].parcel,
                stopFromApi.parcel
              )
            : stopFromApi.parcel,
        };
      } else {
        stops.push({
          ...stopFromApi,
          stopId: stopFromApi.stopId || uuidv4(),
          ...pick(["stopNumber", "sequenceNumber", "displayNumber"], stop),
        });
      }
    }

    if (removedParcels.length) {
      const shopUnsuitable = isShopUnsuitableRequest(firstRequest);
      const stopFromApi = await dispatch(
        validateStopMerge({
          parcels: removedParcels,
          checkOpenRequests: false,
          shopUnsuitable,
        })
      );
      const stopNumber = getHighestStopNumber(stops) + 1;
      const sequenceNumber = getHighestSequenceNumber(stops) + 1;

      stops.push({
        ...stopFromApi,
        ...originalTypes.delivery,
        stopNumber,
        sequenceNumber,
        stopId: stopFromApi.stopId || uuidv4(),
        displayNumber: `${sequenceNumber}`,
      });
    }

    dispatch(setRequestStatus(false));

    const successfullyCreated = await dispatch(
      createRequestAction()(updatedRequests)
    );
    successfullyCreated && dispatch(setStops(stops));
    return successfullyCreated;
  } catch (error) {
    dispatch(setRequestStatus(false));
    dispatch(
      showNotification({
        type: NotificationType.ACTION,
        message: getErrorMessage(error),
        buttonText: DISMISS,
      })
    );
    return false;
  }
};
